import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subscription, forkJoin, map } from 'rxjs';
import lottie, { AnimationItem } from 'lottie-web';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Batch, BatchSummary } from '../../core/services/batch';
import { Connector } from '../../core/services/connector';
import { CodeGraph } from '../../core/services/code-graph';
import { GitHubConnector } from '../../core/services/github-connector';
import { IngestionRealtime } from '../../core/services/ingestion-realtime';
import { Dashboard, Insights } from '../../core/services/dashboard';
import { MailboxService, MailboxConnection } from '../../core/services/mailbox.service';
import { BrandIcon } from '../../shared/components/brand-icon/brand-icon';
import { idsEqual, isPreparing, isProcessing, batchStatusLabel } from '../../shared/constants/batch-status';
import { BrainFlowEngine, applyBrainPalette, combinePalettes, paletteFromSources, recolorBrainNetwork } from './brain-flow';
import {
  DocumentSourceTheme,
  compareDocumentProviders,
  documentProvider,
  themeForProvider
} from './document-sources';

const MAX_ACTIVE_BATCHES = 100;
const RECENT_FEED_SIZE = 8;
const BRAIN_LOTTIE_PATH = '/assets/lottie/Blue%20and%20Orange%20Brain%20Network.json';

interface RepoRow {
  name: string;
  status: 'idle' | 'ingesting' | 'completed' | 'failed';
}

interface DocGroup {
  key: string;
  theme: DocumentSourceTheme;
  connected: boolean;
  online: boolean;
  batches: BatchSummary[];
  live: boolean;
}

@Component({
  selector: 'app-brain',
  standalone: true,
  imports: [CommonModule, Sidebar, Navbar, BrandIcon],
  templateUrl: './brain.html',
  styleUrl: './brain.css'
})
export class Brain implements OnInit, AfterViewInit, OnDestroy {
  private batchService = inject(Batch);
  private connectorService = inject(Connector);
  private codeGraphService = inject(CodeGraph);
  private githubService = inject(GitHubConnector);
  private realtime = inject(IngestionRealtime);
  private dashboardService = inject(Dashboard);
  private mailboxService = inject(MailboxService);

  @ViewChild('brainLottie', { static: true }) brainLottie!: ElementRef<HTMLDivElement>;
  @ViewChild('vizWrap', { static: true }) vizWrap!: ElementRef<HTMLDivElement>;
  @ViewChild('flowCanvas', { static: true }) flowCanvas!: ElementRef<HTMLCanvasElement>;

  private ngZone = inject(NgZone);
  private subscriptions = new Subscription();
  private lottieAnim?: AnimationItem;
  private flowEngine?: BrainFlowEngine;
  private reduceMotion = false;
  private lottieReady = signal(false);

  connectedProviders = signal<Set<string>>(new Set());
  docBatches = signal<BatchSummary[]>([]);
  docExpanded = signal<Record<string, boolean>>({});

  isGitHubConnected = signal(false);
  repos = signal<RepoRow[]>([]);
  ghExpanded = signal(true);

  outlookConnection = signal<MailboxConnection | null>(null);
  emailBatches = signal<BatchSummary[]>([]);
  outlookExpanded = signal(true);

  insights = signal<Insights | null>(null);

  readonly ghLive = computed(() => this.repos().some(r => r.status === 'ingesting'));
  readonly docGroups = computed(() => this.buildDocGroups());
  readonly liveDocGroups = computed(() => this.docGroups().filter(g => g.live));
  readonly spLive = computed(() => this.liveDocGroups().length > 0);
  readonly emailLive = computed(() => this.emailBatches().some(b => this.isBatchActive(b)));
  readonly isFlowing = computed(() => this.ghLive() || this.spLive() || this.emailLive());
  readonly activeDocTheme = computed(() => this.pickDocTheme());
  readonly flowDocPalette = computed(() => {
    const live = this.liveDocGroups();
    if (live.length) return combinePalettes(live.map(g => g.theme.palette));
    return this.activeDocTheme().palette;
  });
  readonly brainPalette = computed(() => paletteFromSources({
    gh: this.ghLive(),
    sp: this.spLive(),
    email: this.emailLive(),
    document: this.flowDocPalette()
  }));
  readonly connectedSourceCount = computed(() => {
    let count = 0;
    if (this.isGitHubConnected()) count++;
    if (this.outlookConnection()) count++;
    const providers = this.connectedProviders();
    for (const key of ['SharePoint', 'GoogleDrive', 'Slack', 'Teams', 'Ftp']) {
      if (providers.has(key)) count++;
    }
    return count;
  });

  constructor() {
    effect(() => {
      const ready = this.lottieReady();
      const gh = this.ghLive();
      const sp = this.spLive();
      const email = this.emailLive();
      const document = this.flowDocPalette();
      const flowing = gh || sp || email;
      if (!ready) return;
      this.syncBrainMotion(flowing);
      this.flowEngine?.setState({ gh, sp, email, document });
    });
  }

  ngOnInit(): void {
    this.loadSharePoint();
    this.loadGitHub();
    this.loadOutlook();
    this.loadInsights();
    this.subscribeRealtime();
  }

  ngAfterViewInit(): void {
    this.initLottie();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.flowEngine?.stop();
    this.flowEngine = undefined;
    this.lottieAnim?.destroy();
    this.lottieAnim = undefined;
    [...this.docBatches(), ...this.emailBatches()].forEach(b => this.realtime.leaveBatch(b.id));
  }

  private initLottie(): void {
    const host = this.brainLottie?.nativeElement;
    if (!host) return;

    this.reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    void fetch(BRAIN_LOTTIE_PATH)
      .then(res => res.json())
      .then(data => {
        this.lottieAnim = lottie.loadAnimation({
          container: host,
          renderer: 'svg',
          loop: !this.reduceMotion,
          autoplay: !this.reduceMotion,
          animationData: recolorBrainNetwork(data),
          rendererSettings: { preserveAspectRatio: 'xMidYMid meet' }
        });

        if (this.reduceMotion) this.lottieAnim.goToAndStop(40, true);

        this.lottieAnim.addEventListener('enterFrame', () => {
          const hostEl = this.brainLottie?.nativeElement;
          if (!hostEl) return;
          applyBrainPalette(hostEl, this.brainPalette(), !this.isFlowing());
        });

        const boot = () => {
          this.ngZone.run(() => {
            this.initFlowEngine();
            this.lottieReady.set(true);
          });
        };
        this.lottieAnim.addEventListener('DOMLoaded', boot);
        if (host.querySelector('svg')) boot();
      })
      .catch(() => {
        this.lottieAnim = lottie.loadAnimation({
          container: host,
          renderer: 'svg',
          loop: !this.reduceMotion,
          autoplay: !this.reduceMotion,
          path: BRAIN_LOTTIE_PATH,
          rendererSettings: { preserveAspectRatio: 'xMidYMid meet' }
        });
        this.initFlowEngine();
        this.ngZone.run(() => this.lottieReady.set(true));
      });
  }

  private initFlowEngine(): void {
    const canvas = this.flowCanvas?.nativeElement;
    const wrap = this.vizWrap?.nativeElement;
    const host = this.brainLottie?.nativeElement;
    if (!canvas || !wrap || !host || this.flowEngine) return;

    this.flowEngine = new BrainFlowEngine(canvas, wrap, host, this.reduceMotion);
    this.flowEngine.setState({
      gh: this.ghLive(),
      sp: this.spLive(),
      email: this.emailLive(),
      document: this.flowDocPalette()
    });
    this.ngZone.runOutsideAngular(() => this.flowEngine?.start());
  }

  private syncBrainMotion(flowing: boolean): void {
    const host = this.brainLottie?.nativeElement;
    const anim = this.lottieAnim;
    if (!host || !anim) return;
    const palette = this.brainPalette();

    if (this.reduceMotion) {
      anim.goToAndStop(24, true);
      applyBrainPalette(host, palette, true);
      return;
    }

    if (flowing) {
      anim.setSpeed(1.35);
      anim.play();
      applyBrainPalette(host, palette, false);
      return;
    }

    anim.pause();
    requestAnimationFrame(() => applyBrainPalette(host, palette, true));
  }

  private loadSharePoint(): void {
    this.connectorService.getAll().subscribe({
      next: (res) => {
        const connected = new Set(
          (res.data ?? []).filter(c => c.status === 'Connected').map(c => c.provider)
        );
        this.connectedProviders.set(connected);
      },
      error: () => this.connectedProviders.set(new Set())
    });

    this.loadFeed(b => b.sourceType !== 'Mailbox' && b.sourceType !== 'Github' && b.sourceType !== 'GitHub').subscribe({
      next: (docs) => {
        this.docBatches.set(docs);
        docs.forEach(b => void this.realtime.joinBatch(b.id));
      },
      error: () => this.docBatches.set([])
    });
  }

  private loadOutlook(): void {
    this.mailboxService.getConnections().subscribe({
      next: (res) => {
        this.outlookConnection.set((res.data ?? []).find(c => c.providerName === 'Outlook') ?? null);
      },
      error: () => this.outlookConnection.set(null)
    });

    this.loadFeed(b => b.sourceType === 'Mailbox').subscribe({
      next: (mail) => {
        this.emailBatches.set(mail);
        mail.forEach(b => void this.realtime.joinBatch(b.id));
      },
      error: () => this.emailBatches.set([])
    });
  }

  private loadFeed(filter: (b: BatchSummary) => boolean): Observable<BatchSummary[]> {
    return forkJoin([
      this.batchService.getBatches('Processing', 1, MAX_ACTIVE_BATCHES),
      this.batchService.getBatches(undefined, 1, RECENT_FEED_SIZE),
    ]).pipe(
      map(([active, recent]) => {
        const merged = new Map<string, BatchSummary>();
        for (const b of active.data.items.filter(filter)) merged.set(b.id, b);
        for (const b of recent.data.items.filter(filter)) {
          if (merged.size >= RECENT_FEED_SIZE) break;
          if (!merged.has(b.id)) merged.set(b.id, b);
        }
        return Array.from(merged.values());
      })
    );
  }

  private loadGitHub(): void {
    this.githubService.getConnections().subscribe({
      next: (res) => {
        const connections = res.data.connections || [];
        this.isGitHubConnected.set(connections.length > 0);
        if (connections.length === 0) {
          this.repos.set([]);
          return;
        }
        forkJoin(connections.map(c => this.codeGraphService.getSelectedRepos(c.id))).subscribe({
          next: (results) => {
            const allRepos: RepoRow[] = [];
            results.forEach(res => {
              const statusMap = new Map((res.repos || []).map(r => [r.repoName, r.status]));
              (res.repoNames || []).forEach(name => allRepos.push({
                name,
                status: (statusMap.get(name) ?? 'idle') as RepoRow['status']
              }));
            });
            this.repos.set(allRepos);
          },
          error: () => this.repos.set([])
        });
      },
      error: () => {
        this.isGitHubConnected.set(false);
        this.repos.set([]);
      }
    });
    void this.realtime.joinCodeGraphGroup();
  }

  private loadInsights(): void {
    this.dashboardService.getInsights().subscribe({
      next: (res) => this.insights.set(res.data),
      error: () => this.insights.set(null)
    });
  }

  private subscribeRealtime(): void {
    this.subscriptions.add(
      this.realtime.batchProgress$.subscribe(payload => {
        const updater = (items: BatchSummary[]) => items.map(b => !idsEqual(b.id, payload.batchId) ? b : {
          ...b,
          status: b.status === 'Completed' || b.status === 'Failed' ? b.status : 'Processing',
          totalDocuments: payload.total ?? b.totalDocuments,
          processedDocuments: payload.processed ?? b.processedDocuments,
          failedDocuments: payload.failed ?? b.failedDocuments,
          percentComplete: payload.percentComplete ?? b.percentComplete
        });
        this.docBatches.update(updater);
        this.emailBatches.update(updater);
      })
    );

    this.subscriptions.add(
      this.realtime.batchStarted$.subscribe(payload => {
        const status = this.normalizeBatchStatus(payload.status);
        const applyStart = (items: BatchSummary[]) => items.map(b =>
          idsEqual(b.id, payload.batchId) ? { ...b, status } : b
        );
        this.docBatches.update(applyStart);
        this.emailBatches.update(applyStart);

        const knownSp = this.docBatches().some(b => idsEqual(b.id, payload.batchId));
        const knownMail = this.emailBatches().some(b => idsEqual(b.id, payload.batchId));
        if (!knownSp && !knownMail) {
          this.loadSharePoint();
          this.loadOutlook();
        }
      })
    );

    this.subscriptions.add(
      this.realtime.batchCompleted$.subscribe(payload => {
        const status = this.normalizeBatchStatus(payload.status);
        const updater = (items: BatchSummary[]) => items.map(b => !idsEqual(b.id, payload.batchId) ? b : {
          ...b,
          status,
          completedAt: payload.completedAt ?? b.completedAt,
          percentComplete: status === 'Completed' || status === 'Failed' ? 100 : b.percentComplete
        });
        this.docBatches.update(updater);
        this.emailBatches.update(updater);
        this.loadInsights();
      })
    );

    this.subscriptions.add(
      this.realtime.batchCreationCompleted$.subscribe(payload => {
        const status = this.normalizeBatchStatus(payload.status);
        const updater = (items: BatchSummary[]) => items.map(b => !idsEqual(b.id, payload.batchId) ? b : {
          ...b,
          status,
          totalDocuments: payload.totalDocuments ?? b.totalDocuments
        });
        this.docBatches.update(updater);
        this.emailBatches.update(updater);
      })
    );

    this.subscriptions.add(
      this.realtime.batchCreationFailed$.subscribe(payload => {
        const updater = (items: BatchSummary[]) => items.map(b =>
          idsEqual(b.id, payload.batchId) ? { ...b, status: 'Failed' } : b
        );
        this.docBatches.update(updater);
        this.emailBatches.update(updater);
      })
    );

    this.subscriptions.add(
      this.realtime.repoIngestionStarted$.subscribe(payload => {
        this.repos.update(items => items.map(r => r.name !== payload.repoName ? r : { ...r, status: 'ingesting' }));
      })
    );

    this.subscriptions.add(
      this.realtime.repoIngestionCompleted$.subscribe(payload => {
        this.repos.update(items => items.map(r => r.name !== payload.repoName ? r : {
          ...r, status: payload.success ? 'completed' : 'failed'
        }));
        this.loadInsights();
      })
    );
  }

  toggleDoc(key: string) {
    this.docExpanded.update(map => ({ ...map, [key]: map[key] === false }));
  }
  isDocExpanded(key: string): boolean {
    return this.docExpanded()[key] !== false;
  }
  toggleGH() { this.ghExpanded.update(v => !v); }
  toggleOutlook() { this.outlookExpanded.update(v => !v); }

  isBatchActive(b: BatchSummary) {
    return isProcessing(b.status) || isPreparing(b.status);
  }

  private normalizeBatchStatus(status: string | number | null | undefined): string {
    if (typeof status === 'number') return batchStatusLabel(status);
    switch ((status ?? '').toLowerCase()) {
      case 'completed': return 'Completed';
      case 'failed': return 'Failed';
      case 'draft':
      case 'created': return 'Draft';
      case 'processing': return 'Processing';
      case 'enumerating': return 'Preparing';
      default: return status ?? 'Processing';
    }
  }

  batchLabel(b: BatchSummary): string {
    if (b.status === 'Completed') return 'Completed';
    if (b.status === 'Failed') return 'Failed';
    if (isPreparing(b.status)) return 'Preparing';
    if (!isProcessing(b.status)) return 'Draft';
    return `${Math.round(b.percentComplete)}%`;
  }

  activeRepoNames() { return this.repos().filter(r => r.status === 'ingesting').map(r => r.name); }

  timeAgo(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  groupProgress(group: DocGroup): string {
    const active = group.batches.filter(b => this.isBatchActive(b));
    const processed = active.reduce((sum, b) => sum + b.processedDocuments + b.failedDocuments, 0);
    const total = active.reduce((sum, b) => sum + b.totalDocuments, 0);
    return `${processed}/${total} docs`;
  }

  emailProgressLabel(): string {
    const a = this.emailBatches().filter(b => this.isBatchActive(b));
    const p = a.reduce((s, b) => s + b.processedDocuments + b.failedDocuments, 0);
    const t = a.reduce((s, b) => s + b.totalDocuments, 0);
    return `${p}/${t} emails`;
  }

  ghProgressLabel(): string {
    const flowing = this.activeRepoNames();
    return `${flowing.length} ${flowing.length === 1 ? 'repo' : 'repos'}`;
  }

  trackByRepoName(_: number, r: RepoRow): string { return r.name; }
  trackByBatchId(_: number, b: BatchSummary): string { return b.id; }
  trackByGroup(_: number, group: DocGroup): string { return group.key; }

  private buildDocGroups(): DocGroup[] {
    const byProvider = new Map<string, BatchSummary[]>();
    for (const batch of this.docBatches()) {
      const provider = documentProvider(batch);
      if (!provider) continue;
      const list = byProvider.get(provider) ?? [];
      list.push(batch);
      byProvider.set(provider, list);
    }

    const keys = new Set<string>(byProvider.keys());
    for (const provider of this.connectedProviders()) {
      if (provider === 'SharePoint' || provider === 'GoogleDrive' || provider === 'Slack' || provider === 'Teams' || provider === 'Ftp') {
        keys.add(provider);
      }
    }

    const groups = [...keys]
      .sort(compareDocumentProviders)
      .map(key => {
        const batches = byProvider.get(key) ?? [];
        const connected = this.connectedProviders().has(key);
        return {
          key,
          theme: themeForProvider(key),
          connected,
          online: key === 'Upload' ? batches.length > 0 : connected,
          batches,
          live: batches.some(b => this.isBatchActive(b))
        };
      })
      .filter(group => group.connected || group.batches.length > 0);

    if (!groups.length) {
      return [{
        key: 'SharePoint',
        theme: themeForProvider('SharePoint'),
        connected: false,
        online: false,
        batches: [],
        live: false
      }];
    }
    return groups;
  }

  private pickDocTheme(): DocumentSourceTheme {
    const live = this.liveDocGroups();
    const pool = live.length ? live : this.docGroups().filter(g => g.batches.length);
    if (!pool.length) return themeForProvider('SharePoint');
    const ranked = [...pool].sort((a, b) => this.newestBatchTime(b) - this.newestBatchTime(a));
    return ranked[0].theme;
  }

  private newestBatchTime(group: DocGroup): number {
    const batches = group.live ? group.batches.filter(b => this.isBatchActive(b)) : group.batches;
    return batches.reduce((latest, batch) => {
      const stamp = new Date(batch.startedAt || batch.createdAt).getTime();
      return Number.isNaN(stamp) ? latest : Math.max(latest, stamp);
    }, 0);
  }
}
